import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useSound } from "@/context/SoundContext";
import { Chip, Composer } from "./ContactUI";
import { classify, loadAttempt } from "./attemptClient";
import { ADULT, SUPPORT_TARGETS, UNSURE_OPTIONS, journeySummary, recoveryBranches, reportReasons } from "./phaseE";
import { WildRushHome } from "./WildRushHome";

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Tell Someone sequence (Master 47–59). Interface-led, first-person framing, no realistic people.
// Adult wording is authored (phaseE.js ADULT). Child free text is classified only; never stored.
export default function SupportStage({ sid, screen, state, engine }) {
  const reduce = useReducedMotion();
  const { setAmbientScene } = useSound();
  const [lines, setLines] = useState([]);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sub, setSub] = useState(null); // 56: null | "report" | "privacy" | "unsure"
  const [branchIdx, setBranchIdx] = useState(0);
  const [actions, setActions] = useState(state.recovery_actions || []);
  const [ev, setEv] = useState(new Set()); // bounded event names only, for a truthful Screen 52 summary
  const run = useRef({ live: true });
  const branches = recoveryBranches(state);

  const say = async (items, token) => {
    for (const l of items) {
      await wait(reduce ? 120 : 900);
      if (!token.live) return;
      setLines((x) => [...x, l]);
    }
  };

  useEffect(() => {
    const token = { live: true }; run.current = token;
    setLines([]); setReady(false); setSub(null);
    try { setAmbientScene("calm"); } catch {}
    let opener = [];
    if (screen === 47) opener = [{ who: "scene", text: "The game gets smaller. The room around you comes back." }];
    if (screen === 49) opener = [{ who: "adult", text: "Sometimes it isn't obvious who to talk to." }];
    if (screen === 51) opener = ADULT.first.map((t) => ({ who: "adult", text: t }));
    if (screen === 52) opener = [{ who: "adult", text: ADULT.look }];
    if (screen === 53) { const b = ADULT[branches[branchIdx]]; opener = b ? [{ who: "you", text: b.child }, ...b.adult.map((t) => ({ who: "adult", text: t }))] : []; }
    if (screen === 56) opener = [{ who: "adult", text: ADULT.now }];
    if (screen === 51) engine.event("support_response_seen", 51);
    if (screen === 52) loadAttempt(sid).then((d) => { if (token.live && d?.events) setEv(new Set(d.events.map((e) => e.name))); }).catch(() => {});
    (async () => { await say(opener, token); if (token.live) setReady(true); })();
    return () => { token.live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, branchIdx]);

  const go = async (next, patch, events = []) => {
    setBusy(true);
    if (patch) await engine.patch(patch);
    events.forEach((e, i) => engine.event(e, screen, `-${i}`));
    engine.goto(next);
  };

  const pickTarget = (id) => go(id === "unsure" ? 49 : 50, { help_seeking_target: id }, ["support_person_selected"]);
  const pickUnsure = () => go(50, null, ["possible_support_identified"]);
  const onSay = async (text) => {
    setBusy(true); setLines((x) => [...x, { who: "you", text }]);
    const res = await classify(sid, 50, "disclosure", text);
    if (!run.current.live) return;
    if (res.safeguarding_override) { await engine.pause(); return; }
    await go(51, { disclosure_attempted: true }, ["disclosure_attempted"]);
  };
  const afterSummary = () => go(branches.length ? 53 : 56);
  const nextBranch = () => { if (branchIdx + 1 < branches.length) { setBranchIdx((i) => i + 1); setLines([]); setReady(false); } else go(56); };
  const addAction = async (a, extraPatch = {}, events = []) => {
    const list = Array.from(new Set([...actions, a])); setActions(list);
    await engine.patch({ recovery_actions: list, ...extraPatch }); events.forEach((e, i) => engine.event(e, screen, `-${a}${i}`));
  };
  const toolkit = [
    !state.blocked && { id: "tk-block", label: "Block the account" },
    !state.reported && { id: "tk-report", label: "Report the account" },
    { id: "tk-show", label: "Save / show the messages" }, { id: "tk-privacy", label: "Check privacy settings" }, { id: "tk-unsure", label: "I'm not sure" },
  ].filter(Boolean);
  const onTool = async (id) => {
    if (id === "tk-block") { await addAction("block", { blocked: true }, ["contact_blocked"]); setLines((x) => [...x, { who: "sys", text: "Account blocked" }]); }
    if (id === "tk-report") setSub("report");
    if (id === "tk-show") { await addAction("show_messages"); setLines((x) => [...x, { who: "adult", text: "Good — keep these. We don't need to delete anything yet." }]); }
    if (id === "tk-privacy") setSub("privacy");
    if (id === "tk-unsure") {
      setLines((x) => [...x, ...ADULT.unsure.map((t) => ({ who: "adult", text: t }))]);
      await wait(reduce ? 200 : 1200);
      if (!state.blocked) { await addAction("block", { blocked: true }, ["contact_blocked"]); setLines((x) => [...x, { who: "sys", text: "Account blocked" }]); }
      else if (!state.reported) setSub("report");
    }
  };
  const submitReport = async () => { await addAction("report", { reported: true }, ["contact_reported", "report_reason_selected"]); setSub(null); setLines((x) => [...x, { who: "sys", text: "Report received" }]); };
  const savePrivacy = async () => { await addAction("privacy_check", {}, ["privacy_check_opened"]); setSub(null); setLines((x) => [...x, { who: "sys", text: "Settings updated" }]); };

  return (
    <div className="relative flex h-[100dvh] w-full flex-col bg-[#0a0a0a] text-white" data-testid="support-stage" data-screen={screen} style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(120,90,60,0.18),transparent_60%)]" aria-hidden="true" />
      <div className="absolute left-4 top-4 z-20 sm:left-6 sm:top-6"><WildRushHome /></div>
      <div className="relative mx-auto flex w-full max-w-lg flex-1 flex-col px-5 pt-6 sm:pt-10" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
        <motion.div initial={{ scale: 1, opacity: 0.9 }} animate={{ scale: 0.92, opacity: 1 }} transition={{ duration: reduce ? 0 : 1.2 }} className="mx-auto w-40 rounded-2xl border border-white/10 bg-[#0f1a20] px-3 py-2 text-center text-[10px] font-bold tracking-[0.22em] text-white/50" data-testid="device-frame">WILD RUSH · PAUSED</motion.div>
        <div className="mt-6 flex flex-1 flex-col gap-3 overflow-y-auto" role="log" aria-live="polite" data-testid="support-thread">
          {lines.map((l, i) => (
            <motion.p key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`support-line-${l.who}`}
              className={l.who === "adult" ? "max-w-[88%] rounded-2xl bg-[#f3e6d6]/12 px-4 py-3 text-[15px] leading-relaxed text-white/90" : l.who === "you" ? "self-end rounded-2xl bg-[#4FA3D9]/80 px-4 py-2.5 text-[15px]" : l.who === "sys" ? "self-center rounded-full bg-white/10 px-3 py-1 text-xs text-white/70" : "text-[15px] italic text-white/60"}>{l.text}</motion.p>
          ))}
          {screen === 52 && ready && (
            <ul className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80" data-testid="journey-summary">
              {journeySummary(state, ev).map((t) => <li key={t} className="py-1">· {t}</li>)}
            </ul>
          )}
        </div>
        {ready && !busy && (
          <div className="mt-4 flex flex-col gap-3">
            {screen === 47 && <Chip id="support-continue" label="Continue" onClick={() => go(48)} />}
            {screen === 48 && <><p className="text-lg font-semibold" data-testid="support-question">Who would you want to talk to?</p><div className="flex flex-wrap gap-2">{SUPPORT_TARGETS.map((t) => <Chip key={t.id} id={`target-${t.id}`} label={t.label} quiet onClick={() => pickTarget(t.id)} />)}</div></>}
            {screen === 49 && <><p className="text-sm text-white/65">Who would help you without blaming you?</p><div className="flex flex-wrap gap-2">{UNSURE_OPTIONS.map((o, i) => <Chip key={o} id={`unsure-${i}`} label={o} quiet onClick={pickUnsure} />)}</div></>}
            {screen === 50 && <><p className="text-sm text-white/65">Say something…</p><Composer onSend={onSay} disabled={busy} autoFocus /></>}
            {screen === 51 && <Chip id="support-continue" label="Continue" onClick={() => go(52)} />}
            {screen === 52 && <Chip id="support-continue" label="Continue" onClick={afterSummary} />}
            {screen === 53 && <Chip id="support-continue" label="Continue" onClick={nextBranch} />}
            {screen === 56 && !sub && <><div className="flex flex-wrap gap-2">{toolkit.map((t) => <Chip key={t.id} id={t.id} label={t.label} quiet onClick={() => onTool(t.id)} />)}</div><Chip id="support-finish" label="That's enough for now" onClick={() => go(61)} /></>}
            {screen === 56 && sub === "report" && <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="report-together"><p className="text-sm font-semibold">Why are you reporting this account?</p><div className="mt-3 flex flex-wrap gap-2">{reportReasons(56).map((r, i) => <Chip key={r} id={`reason-${i}`} label={r} quiet onClick={submitReport} />)}</div></div>}
            {screen === 56 && sub === "privacy" && <PrivacyPanel onSave={savePrivacy} />}
          </div>
        )}
      </div>
    </div>
  );
}

// Fictional privacy panel (Master 59) — practice step, not a lesson. No real settings are changed.
const PrivacyPanel = ({ onSave }) => {
  const [fr, setFr] = useState("Everyone"); const [pm, setPm] = useState("Everyone");
  const Opt = ({ v, cur, set, id }) => <button onClick={() => set(v)} aria-pressed={cur === v} data-testid={id} className={`rounded-full border px-3 py-1.5 text-xs ${cur === v ? "border-[#4FA3D9] bg-[#4FA3D9]/20" : "border-white/15 text-white/70"}`}>{v}</button>;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid="privacy-panel">
      <p className="text-sm text-white/80">{ADULT.privacy}</p>
      <p className="mt-3 text-xs font-semibold text-white/55">Who can send friend requests?</p>
      <div className="mt-2 flex flex-wrap gap-2">{["Everyone", "Friends of friends", "Friends only"].map((v) => <Opt key={v} v={v} cur={fr} set={setFr} id={`fr-${v.replace(/\s/g, "-").toLowerCase()}`} />)}</div>
      <p className="mt-3 text-xs font-semibold text-white/55">Who can send private messages?</p>
      <div className="mt-2 flex flex-wrap gap-2">{["Everyone", "Friends only"].map((v) => <Opt key={v} v={v} cur={pm} set={setPm} id={`pm-${v.replace(/\s/g, "-").toLowerCase()}`} />)}</div>
      <div className="mt-4"><Chip id="privacy-save" label="Save" onClick={onSave} /></div>
    </div>
  );
};
